import { Form } from "@circle-react-uikit/Form";
import { useSpacesContext } from "@circle-react/contexts/spacesContext";
import { reactQueryPut } from "@circle-react/helpers/backendRequestHelpers";
import { afterSaveRedirectPath } from "@circle-react/helpers/postHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { Post } from "@circle-react/types/Post";
import { Modal } from "@circleco/compass/components/Modal";
import { useNavigate } from "@tanstack/react-router";
import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { SpaceSelectCompass } from "@/react/components/shared/SpaceSelectCompass";
import type { ApiError } from "@/react/config/CustomErrors";

export interface DuplicatePostModalComponentProps {
  hasNoteAboutExceptions?: boolean;
  onDuplicateSuccess?: (post: Post) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  postId: number;
  postSlug: string;
  postTitle: string;
  spaceId: number;
}

interface FormData {
  send_notifications: boolean;
  space_id: number;
}

const formId = "duplicate-post-form";

export const DuplicatePostModalComponent = ({
  spaceId,
  postId,
  postSlug,
  postTitle,
  hasNoteAboutExceptions = true,
  open,
  onOpenChange,
  onDuplicateSuccess,
}: DuplicatePostModalComponentProps) => {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const {
    isLoading: isSpacesLoading,
    helpers: { basicSpacesWithPostCreationAllowed },
  } = useSpacesContext();
  const spaces = basicSpacesWithPostCreationAllowed();

  const { mutate: duplicate, isPending: isDuplicating } = useMutation<
    Post,
    ApiError,
    FormData
  >({
    mutationFn: data =>
      reactQueryPut(internalApi.posts.duplicate({ spaceId, postSlug }), {
        post: {
          post_id: postId,
          space_id: data.space_id,
          send_notifications: data.send_notifications,
        },
      }),
    onSuccess: post => {
      success(t("duplicated_successfully"));
      onOpenChange(false);
      void navigate({
        href: afterSaveRedirectPath({ post, editMode: true }),
      });
      onDuplicateSuccess?.(post);
    },
    onError: () => {
      error(t("duplication_failed"));
    },
  });

  return (
    <Modal.Root open={open} onOpenChange={onOpenChange} size="md">
      <Modal.Content>
        <Modal.Header
          title={t("duplicate_post_title", {
            post_title: postTitle || t("duplicate_post_title_untitled"),
          })}
        />
        <Modal.Body>
          <Form<FormData>
            onSubmit={data => duplicate(data)}
            defaultValues={{ space_id: spaceId, send_notifications: false }}
            id={formId}
          >
            <Form.Item
              name="space_id"
              isFirstChild
              hideBorder
              fullWidth
              label={t("space")}
              description={t("duplicate_post_to_space")}
              rules={{
                required: t("events.create.form.space_required_error"),
              }}
            >
              <SpaceSelectCompass
                spaces={spaces}
                name="space_id"
                defaultValue={spaceId}
                isLoading={isSpacesLoading}
                disabled={isSpacesLoading}
              />
            </Form.Item>
            {hasNoteAboutExceptions && (
              <>
                <Typography.LabelXs color="text-light">
                  {t("comments_wont_be_duplicated")}
                </Typography.LabelXs>
                <Form.Item
                  name="send_notifications"
                  hideBorder
                  fullWidth
                  label={t("duplicate_post_send_notifications")}
                  hideDescription
                  inlineReverse
                >
                  <Form.ToggleSwitch
                    variant="small"
                    name="send_notifications"
                  />
                </Form.Item>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer
          secondaryAction={{
            label: t("cancel"),
          }}
          primaryAction={{
            label: isDuplicating ? t("duplicating") : t("duplicate"),
            type: "submit",
            form: formId,
            disabled: isSpacesLoading,
            loading: isDuplicating,
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
